import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { AuthLayout, AuthLink } from "@/components/auth/AuthLayout";
import { authService } from "@/services/authService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AUTH } from "@/constants/testIds";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) return setError("This reset link is invalid or missing its token.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await authService.resetPassword(token, password);
      navigate("/login", { replace: true, state: { reset: true } });
    } catch (err) {
      setError(err.message || "Unable to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Enter and confirm your new password."
      footer={<AuthLink to="/login">Back to sign in</AuthLink>}
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid={AUTH.resetPage} noValidate>
        {error && (
          <Alert variant="destructive" data-testid={AUTH.error}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="password">New password</Label>
          <Input id="password" type="password" autoComplete="new-password" required value={password}
            onChange={(e) => setPassword(e.target.value)} data-testid={AUTH.password} placeholder="At least 8 characters" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm new password</Label>
          <Input id="confirm" type="password" autoComplete="new-password" required value={confirm}
            onChange={(e) => setConfirm(e.target.value)} data-testid={AUTH.confirmPassword} placeholder="Re-enter password" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid={AUTH.submit}>
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Updating…</> : "Update password"}
        </Button>
      </form>
    </AuthLayout>
  );
}
